import { useEffect, useRef, useState } from 'react';
import useLegacyBusinessDataFetch from './useLegacyBusinessDataFetch';
import FileExplorer from './Components/FileExplorer/FileExplorer';
import styles from './DataRoom.module.scss';
import LoadingSpinner from '../../common/_custom/LoadingSpinner/LoadingSpinner';
import { createPortal } from 'react-dom';
import useCallApiToFetchFileData from './useCallApiToFetchFileData';
import { ACCESS_TIERS, ACCESS_TYPES } from '../../store/investor/constants';
import { useAppSelector } from '../../app/hooks';
import { Mixpanel } from '../../utils/mixpanel';
import { AICA_DATA_ROOM_OPENED } from '../../utils/constants/mixpanelEvents/investorEvents';
import UnlockScanButton from '../Dashboard/DashboardModules/Overview/components/Buttons/UnlockScan/UnlockScanButton';
import { useDispatch } from 'react-redux';
import { setDealDrawer, setShowUpgradeToScan, updateNeedInfoFileNumber } from '../../store/investor/action';
import AddToScanButton from '../Dashboard/DashboardModules/Overview/components/Buttons/AddToScan/AddToScanButton';
import { USER_TYPE } from '../../utils/constants/user';
import { pendingDataComplete, PendingDataIncomplete } from '../../assets/hostedassets';
import ManageDealDrawer from '../../common/ManageDealDrawer/ManageDealDrawer';
import { getDataGapDocs } from '../../common/MagicUpload/MagicUploadHelper';
import { useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import DataRoomModals from './Components/Modals/DataRoomModals';
import MagicUploadNotifications from '../../common/MagicUploadNotifications/MagicUploadNotifications';
import AskAicaV2 from '../Dashboard/DashboardModules/Overview/components/AskAicaV2/AskAicaV2';

export type FileNode = {
    name: string;
    type: 'file' | 'folder';
    docId?: number;
    updatedAt: string;
    innerFiles?: FileNode[];
    move?: boolean;
    contextId?: string;
    isNew?: any;
    newFilePresent?: boolean;
    metaData?: Record<string, any>;
    signedDocId?: string | null;
    hasSignedCopy?: boolean;
};

const DataRoom = () => {
    const { exportData, isUserRestricted, selectedCompany, refreshReportStatus, downloadPDFStatus, goBackToPortfolio } =
        useLegacyBusinessDataFetch();
    const header = document.getElementById('header') ?? document.createElement('div');
    const {
        fileData,
        isFetchingData,
        fetchFiles,
        activeFilecount,
        archivedFilecount,
        mcaRecordsFileCount,
        dataTypesSubFolderList,
        newFilePresent,
        setIsFetchingData,
        // , isRecurCompany
    } = useCallApiToFetchFileData(selectedCompany?.orgName);

    const investorData = useAppSelector((state) => state.investor);
    const permissions = investorData.permissions;
    const isScanLockedForUser = permissions?.[ACCESS_TIERS.SCAN] === ACCESS_TYPES.LOCKED;
    const dispatch = useDispatch();
    const [showInviteCompanyModal, setShowInviteCompanyModal] = useState(false);
    const [showAddToScanModal, setShowAddToScanModal] = useState('');
    const [showDataRoomModal, setShowDataRoomModal] = useState<{
        showModal: boolean;
        docId?: number;
        currentDocType?: string;
        selectedDocIds?: number[];
        onMoveSuccess?: () => void;
    }>({
        showModal: false,
        docId: undefined,
        currentDocType: '',
        selectedDocIds: undefined,
        onMoveSuccess: undefined,
    });
    const isDemoProfile = !!selectedCompany?.demoOrg;
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const user = useAppSelector((state) => state.user);
    const investorSelectedCompany = useAppSelector((state) => state.investor.selectedCompany);
    const dataRoomOpenedTrackedRef = useRef(false);
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const showDealDrawer = useAppSelector((state) => state.investor.dealDrawer).open;
    const showDataGapCTA = permissions.showDataGap;

    // const getPendingDocumentsCount = async () => {
    //     await getDataGapDocs(
    //         {
    //             assesseeOrgId: params.id ?? '',
    //         },
    //         {
    //             onSuccess: (res) => {
    //                 dispatch(updateNeedInfoFileNumber(res.pendingCount));
    //             },
    //         },
    //     );
    // };
    //
    // useEffect(() => {
    //     getPendingDocumentsCount();
    // }, []);

    const renderHeaderButton = () => {
        if (isUserRestricted === false) {
            if (isScanLockedForUser) return <UnlockScanButton onClick={() => dispatch(setShowUpgradeToScan(ACCESS_TIERS.SCAN))} />;
            else if (selectedCompany?.companyAccessType == ACCESS_TIERS.WATCH)
                return (
                    <AddToScanButton
                        onClick={() => {
                            setShowInviteCompanyModal(true);
                            setShowAddToScanModal('scan');
                        }}
                        accessType={selectedCompany?.companyAccessType}
                        isDemoProfile={isDemoProfile}
                    />
                );
            else if (user.userData.accountType === USER_TYPE.INVESTOR && !isDemoProfile && showDataGapCTA) {
                return (
                    <>
                        <div
                            className={clsx({
                                // [styles.PedingCTA]: (pendingCount as number) > 0 || pendingCount === null,
                                // [styles.PendingDone]: pendingCount === 0,
                                [styles.PedingCTA]: true,
                            })}
                            onClick={() => {
                                searchParams.set('borrowerReq', 'true');
                                setSearchParams(searchParams);
                            }}
                        >
                            <img src={PendingDataIncomplete} alt="pendingDataComplete" />
                            <span>Data Pending</span>
                        </div>
                    </>
                );
            }
        }
    };

    const openCreateDealDrawer = () => {
        dispatch(
            setDealDrawer({
                open: true,
                companyId: selectedCompany?.assesseeOrgId,
                companyName: selectedCompany?.orgName,
                profileGenerationStatus: selectedCompany?.profileGenerationStatus,
            }),
        );
    };

    useEffect(() => {
        const companyId = investorSelectedCompany?.assesseeOrgId ?? '';
        const companyName = investorSelectedCompany?.orgName ?? '';

        const canTrack =
            user?.userData &&
            !dataRoomOpenedTrackedRef.current &&
            (params?.id || selectedCompany?.assesseeOrgId || investorSelectedCompany?.assesseeOrgId);
        if (canTrack) {
            dataRoomOpenedTrackedRef.current = true;
            Mixpanel.track(AICA_DATA_ROOM_OPENED, {
                userEmail: user.userData.primaryEmail,
                companyName,
                userName: user.userData.userName,
                user_id: user.userData.userId,
                user_type: user.userData.accountType,
                companyId,
            });
        }
    }, [investorSelectedCompany, user?.userData, params?.id]);

    return (
        <div className={styles.DataRoomWrapper}>
            {user.userData.accountType !== USER_TYPE.PARTNER && <MagicUploadNotifications source={'FILE'} />}

            {showDealDrawer && <ManageDealDrawer source={dealDrawerState.drawerSource || 'Dataroom Page'} />}

            <div className={styles.DataRoomContainer}>
                {isFetchingData ? (
                    <div className={styles.LoadingContainer}>
                        <LoadingSpinner color={'#4D4D4D'} height={'50px'} />
                    </div>
                ) : (
                    <FileExplorer
                        // isRecurCompany={isRecurCompany ?? false}
                        fetchFiles={fetchFiles ?? (() => {})}
                        activeFilecount={activeFilecount}
                        archivedFilecount={archivedFilecount}
                        mcaRecordsFileCount={mcaRecordsFileCount}
                        exportData={exportData}
                        data={fileData ?? []}
                        selectedCompany={selectedCompany}
                        setShowDataRoomModal={setShowDataRoomModal}
                        newFilePresent={newFilePresent}
                    />
                )}
            </div>
            {showDataRoomModal.showModal && (
                <DataRoomModals
                    setIsFetchingData={setIsFetchingData}
                    showDataRoomModal={showDataRoomModal}
                    setShowDataRoomModal={setShowDataRoomModal}
                    dataTypesSubFolderList={dataTypesSubFolderList}
                    fetchFiles={fetchFiles}
                    modalHeaderTitle={'Move'}
                    onCloseClick={() =>
                        setShowDataRoomModal({
                            showModal: false,
                            currentDocType: '',
                        })
                    }
                />
            )}

            <AskAicaV2 selectedCompany={selectedCompany} global />
        </div>
    );
};

export default DataRoom;
